/* eslint-disable @typescript-eslint/no-empty-function */
import { useTranslation } from 'react-i18next';
import { DateRange } from '../../types/file-manager.type';
import {
  FileType,
  getFileTypeIcon,
  getFileTypeInfo,
  IFileTrashData,
  PaginationState,
} from '../../utils/file-manager';
import { useIsMobile } from 'hooks/use-mobile';
import { useCallback, useEffect, useState } from 'react';
import { TrashTableRowActions } from './trash-files-row-actions';
import { TrashDetailsSheet } from './trash-files-details';
import { CommonGridView } from '../common-grid-view';
import { Trash2 } from 'lucide-react';
import { useTrashedFilesQuery } from '../../hooks/use-document-manager';

interface TrashGridViewProps {
  onRestore?: (file: IFileTrashData) => Promise<void>;
  readonly onPermanentDelete?: (file: IFileTrashData) => Promise<void>;
  onViewDetails?: (file: IFileTrashData) => void;
  filters: {
    name?: string;
    fileType?: FileType;
    deletedBy?: string;
    trashedDate?: DateRange;
  };
  readonly selectedItems?: string[];
  readonly onSelectionChange?: (items: string[]) => void;
  handleSetAllFileIds?: (fileIds: string[]) => void;
}

export const TrashGridView: React.FC<TrashGridViewProps> = (props) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<IFileTrashData | null>(null); // how to keep it generic?
  const [combinedData, setCombinedData] = useState<IFileTrashData[]>([]);
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: isMobile ? 20 : 50,
    totalCount: 0,
  });

  const queryParams = {
    page: 0,
    pageSize: (paginationState.pageIndex + 1) * paginationState.pageSize,
    filter: {
      name: props.filters.name ?? '',
      fileType: props.filters.fileType ?? undefined,
      trashedDate: props.filters.trashedDate,
    }
  };

  const { data, isLoading, error, refetch } = useTrashedFilesQuery(queryParams, {
    softDeleted: true,
  });

  useEffect(() => {
    if (data?.data) {
      props.handleSetAllFileIds?.(data.data.map((file) => file.id));
      setCombinedData(data.data);
    }
  }, [data?.data, paginationState.pageIndex, props.handleSetAllFileIds]);

  useEffect(() => {
    if (data?.totalCount !== undefined) {
      setPaginationState((prev) => ({
        ...prev,
        totalCount: Math.max(0, data.totalCount),
      }));
    }
  }, [data?.totalCount]);

  useEffect(() => {
    setPaginationState((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [props.filters]);

  const handleLoadMore = useCallback(() => {
    if (data && combinedData.length < data.totalCount) {
      setPaginationState((prev) => ({
        ...prev,
        pageIndex: prev.pageIndex + 1,
      }));
    }
  }, [combinedData.length, data]);

  const handleViewDetails = useCallback(
    (file: IFileTrashData) => {
      setSelectedFile(file);
      setIsDetailsOpen(true);
      props.onViewDetails?.(file);
    },
    [props.onViewDetails]
  );

  const handleCloseDetails = useCallback(() => {
    setIsDetailsOpen(false);
    setSelectedFile(null);
  }, []);

  const renderActions = useCallback(
    (file: IFileTrashData) => {
      const mockRow = {
        original: file,
        id: file.id.toString(),
        index: 0,
        getValue: () => { },
        getVisibleCells: () => [],
        getAllCells: () => [],
        getLeftVisibleCells: () => [],
        getRightVisibleCells: () => [],
        getCenterVisibleCells: () => [],
      } as any;

      const handleRestore = async (fileToRestore: IFileTrashData) => {
        try {
          await props.onRestore?.(fileToRestore);
          refetch();
        } catch (error) {
          console.error('Error restoring file:', error);
        }
        if (props.onSelectionChange && props.selectedItems?.includes(fileToRestore.id.toString())) {
          props.onSelectionChange(
            props.selectedItems.filter((id) => id !== fileToRestore.id.toString())
          );
        }
      };

      const handlePermanentDelete = async (fileToDelete: IFileTrashData) => {
        try {
          await props.onPermanentDelete?.(fileToDelete);
          refetch();
        } catch (error) {
          console.error('Error deleting file:', error);
        }
        if (props.onSelectionChange && props.selectedItems?.includes(fileToDelete.id.toString())) {
          props.onSelectionChange(
            props.selectedItems.filter((id) => id !== fileToDelete.id.toString())
          );
        }
      };

      return (
        <TrashTableRowActions
          row={mockRow}
          onRestore={handleRestore}
          onDelete={handlePermanentDelete}
        />
      );
    },
    [props]
  );

  const renderDetailsSheet = useCallback(
    (file: IFileTrashData | null, isOpen: boolean, onClose: () => void) => (
      <TrashDetailsSheet
        isOpen={isOpen}
        onClose={onClose}
        file={
          file
            ? {
              ...file,
              lastModified: file.trashedDate ?? new Date(file.trashedDate ?? Date.now()),
              isShared: file.isShared ?? false,
            }
            : null
        }
        t={t}
      />
    ),
    [t]
  );

  return (
    <CommonGridView
      onViewDetails={handleViewDetails}
      filters={props.filters}
      data={{ data: combinedData, totalCount: paginationState.totalCount }}
      isLoading={isLoading}
      error={error}
      onLoadMore={handleLoadMore}
      renderDetailsSheet={renderDetailsSheet}
      getFileTypeIcon={getFileTypeIcon}
      getFileTypeInfo={getFileTypeInfo}
      renderActions={renderActions}
      emptyStateConfig={{
        icon: Trash2,
        title: t('TRASH_EMPTY'),
        description:
          props.filters.name ||
            props.filters.fileType ||
            props.filters.deletedBy ||
            props.filters.trashedDate
            ? t('NO_FILES_MATCH_CRITERIA')
            : t('NO_DELETED_FILES'),
      }}
      sectionLabels={{
        folder: t('FOLDER'),
        file: t('FILE'),
      }}
      errorMessage={t('ERROR_LOADING_FILES')}
      loadingMessage={t('LOADING')}
      loadMoreLabel={t('LOAD_MORE')}
      processFiles={(files: IFileTrashData[]) => files} // how to give emtpy array of some specific type?
      filterFiles={(files: IFileTrashData[]) => files} // CRITICAL: Make sure this is included
      isDetailsOpen={isDetailsOpen}
      selectedFile={selectedFile}
      handleCloseDetails={handleCloseDetails}
    />
  );
};
